import { KinetiqWithdrawalAbi } from '../abis/kinetiq'
import type { LstWithdrawalRequest } from '../types'
import {
  computeStatus,
  toBigInt,
  toNumber,
  type LstWithdrawalReader,
} from './shared'

/**
 * Kinetiq kHYPE — per-user withdrawal-request enumeration on the
 * StakingManager Diamond.
 *
 * Stage 1: `nextWithdrawalId(user)` (request count) + `withdrawalDelay()`.
 * Stage 2: `withdrawalRequests(user, id)` for `id in [0, count)`.
 *
 * A confirmed/cleared request reads back `hypeAmount == 0` and is
 * skipped. `readyAt = timestamp + withdrawalDelay`.
 */
export const readerKinetiq: LstWithdrawalReader = {
  fetch: async (user, multicallRetry, chainId, entry) => {
    const manager = entry.withdrawalContract
    if (!manager) return []

    let stage1: unknown[]
    try {
      stage1 = (await multicallRetry({
        chain: chainId,
        calls: [
          { address: manager, name: 'nextWithdrawalId', params: [user] },
          { address: manager, name: 'withdrawalDelay', params: [] },
        ] as unknown as never[],
        abi: [KinetiqWithdrawalAbi, KinetiqWithdrawalAbi] as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }
    const count = toNumber(stage1[0]) ?? 0
    const delay = toNumber(stage1[1]) ?? 0
    if (count === 0) return []

    let stage2: unknown[]
    try {
      stage2 = (await multicallRetry({
        chain: chainId,
        calls: Array.from({ length: count }, (_, i) => ({
          address: manager,
          name: 'withdrawalRequests',
          params: [user, BigInt(i)],
        })) as unknown as never[],
        abi: Array.from(
          { length: count },
          () => KinetiqWithdrawalAbi,
        ) as unknown as never,
      })) as unknown[]
    } catch {
      return []
    }

    const out: LstWithdrawalRequest[] = []
    for (let i = 0; i < count; i++) {
      const r = stage2[i] as
        | { hypeAmount?: unknown; timestamp?: unknown }
        | unknown[]
        | undefined
      if (!r) continue
      const hypeAmount = Array.isArray(r)
        ? toBigInt(r[0])
        : toBigInt(r.hypeAmount)
      const timestamp = Array.isArray(r)
        ? toNumber(r[4])
        : toNumber(r.timestamp)
      // Cleared / confirmed requests zero out — skip them.
      if (
        hypeAmount === undefined ||
        hypeAmount === 0n ||
        timestamp === undefined
      ) {
        continue
      }
      const readyAt = timestamp + delay
      out.push({
        lst: entry.lst,
        brand: entry.brand,
        symbol: entry.symbol,
        requestId: String(i),
        amountUnderlying: hypeAmount.toString(),
        status: computeStatus(readyAt),
        readyAt,
      })
    }
    return out
  },
}
